import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isZh = i18n.language === 'zh';

  const navLinks = [
    { label: t('nav.home'), href: '#home' },
    { label: t('nav.services'), href: '#services' },
    { label: t('nav.cases'), href: '#cases' },
    { label: t('nav.contact'), href: '#contact' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleLang = () => {
    i18n.changeLanguage(isZh ? 'en' : 'zh');
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-amber-100'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-[72px]">
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => { e.preventDefault(); handleNav('#home'); }}
          className="flex items-center gap-3 cursor-pointer"
        >
          <img
            src="https://static.readdy.ai/image/d423d7259cd6886cdb573e095a6b9999/a48c44a5812fde7aa1b743301ff8f67a.png"
            alt="Rise Phoenix Logo"
            className="h-10 w-auto object-contain"
          />
          <span
            className={`font-bold text-xl tracking-wide transition-colors duration-300 ${
              scrolled ? 'text-[#1e3a4a]' : 'text-white'
            }`}
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Rise Phoenix
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
              className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap hover:text-amber-600 ${
                scrolled ? 'text-[#2c2c2c]' : 'text-white/90'
              }`}
            >
              {link.label}
            </a>
          ))}

          {/* Language Toggle */}
          <button
            onClick={toggleLang}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all duration-200 text-xs font-semibold cursor-pointer whitespace-nowrap ${
              scrolled
                ? 'border-[#1e3a4a]/30 text-[#1e3a4a] hover:border-amber-500 hover:text-amber-600'
                : 'border-white/40 text-white/90 hover:border-white hover:text-white'
            }`}
            aria-label="Toggle language"
          >
            <i className="ri-translate-2 text-sm"></i>
            <span>{isZh ? 'EN' : '中文'}</span>
          </button>

          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); handleNav('#contact'); }}
            className="ml-1 px-6 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {t('nav.cta')}
          </a>
        </nav>

        {/* Mobile right side */}
        <div className="md:hidden flex items-center gap-3">
          <button
            onClick={toggleLang}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full border text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${
              scrolled ? 'border-[#1e3a4a]/30 text-[#1e3a4a]' : 'border-white/40 text-white'
            }`}
          >
            <i className="ri-translate-2 text-sm"></i>
            <span>{isZh ? 'EN' : '中文'}</span>
          </button>
          <button
            className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
              scrolled ? 'text-[#1e3a4a]' : 'text-white'
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white/98 backdrop-blur-md border-t border-amber-100 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
              className="text-[#2c2c2c] text-sm font-medium py-2 hover:text-amber-600 transition-colors cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); handleNav('#contact'); }}
            className="px-6 py-2 rounded-full bg-amber-500 text-white text-sm font-semibold text-center whitespace-nowrap cursor-pointer"
          >
            {t('nav.cta')}
          </a>
        </div>
      )}
    </header>
  );
}
